import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import FormField from '../../Fundamentals/Form/FormField';
import EnumSelect from '../../Fundamentals/Form/EnumSelect';
import TimeLengthFields from './TimeLengthFields';
import usePlanFormat from './usePlanFormat';
import {
    emptyNotBillableOffset,
    emptyPlanDraft,
    hasSubscriptionUnitMismatch,
    hasUsageLengthMismatch,
    isValidCycleLength,
    isValidLength,
    isValidPlanDraft,
    toCopiedPlanDraft,
    toPlanDraft,
    toPlanRequest,
} from './planUtils';
import {
    VossBindingPeriodEndActionText,
    VossPeriodEndAnchorText,
    VossPeriodOffsetAnchorText,
    VossPlanChangeBehaviourText,
    bindingPeriodEndActions,
    periodEndAnchors,
    periodOffsetAnchors,
    planChangeBehaviours,
} from '../../../Constants/VOSS/vossPlanEnums';
import type { CycleLength, PackagePlan, PackagePlanRequest, PlanBillingCycleDraft, PlanCopySource, PlanDraft, PlanOffsetDraft } from './Packages.types';

interface PackagePlanEditorDrawerProps {
    plan?: PackagePlan;
    copySource?: PlanCopySource;
    // VOSS only accepts name and external id changes once the version has left Draft
    limitedEditing: boolean;
    isSaving: boolean;
    onClose: () => void;
    onSave: (model: PackagePlanRequest) => void;
}

interface OffsetFieldsProps {
    idPrefix: string;
    label: string;
    value: PlanOffsetDraft;
    disabled: boolean;
    onChange: (value: PlanOffsetDraft) => void;
    onRemove?: () => void;
}

const OffsetFields = ({ idPrefix, label, value, disabled, onChange, onRemove }: OffsetFieldsProps) => {
    const { t } = useTranslation();
    const lengthInvalid = !isValidLength(value.length.value, true);
    const valueAnchorInvalid = value.valueAnchor !== null && !isValidLength(value.valueAnchor);

    return (
        <div className="d-flex flex-wrap gap-16 align-items-start">
            <FormField label={label} htmlFor={`${idPrefix}-value`} inline error={lengthInvalid ? (t('packages.offsetInvalid') ?? '') : undefined}>
                <TimeLengthFields id={`${idPrefix}-value`} value={value.length} allowNegative disabled={disabled} onChange={(length) => onChange({ ...value, length })} />
            </FormField>
            <FormField label={t('packages.offsetAnchor') ?? ''} htmlFor={`${idPrefix}-anchor`} inline>
                <EnumSelect
                    id={`${idPrefix}-anchor`}
                    disabled={disabled}
                    value={value.periodAnchor}
                    options={periodOffsetAnchors}
                    getLabel={(anchor) => t(VossPeriodOffsetAnchorText[anchor])}
                    onChange={(periodAnchor) => onChange({ ...value, periodAnchor })}
                />
            </FormField>
            <FormField
                label={t('packages.valueAnchor') ?? ''}
                htmlFor={`${idPrefix}-value-anchor`}
                inline
                help={t('packages.valueAnchorHelp') ?? ''}
                error={valueAnchorInvalid ? (t('packages.offsetInvalid') ?? '') : undefined}
            >
                <div className="d-flex gap-8 align-items-center">
                    <input
                        id={`${idPrefix}-value-anchor`}
                        type="number"
                        min={0}
                        step={1}
                        className="form-control flex-1"
                        disabled={disabled}
                        value={value.valueAnchor ?? ''}
                        onChange={(e) => onChange({ ...value, valueAnchor: e.target.value === '' ? null : Number(e.target.value) })}
                    />
                    {onRemove !== undefined && !disabled && (
                        <button type="button" className="btn btn-tertiary btn-sm flex-shrink-0" onClick={onRemove}>
                            {t('packages.removeNotBillableOffset')}
                        </button>
                    )}
                </div>
            </FormField>
        </div>
    );
};

interface CycleSectionProps {
    icon: string;
    title: string;
    summary: string;
    defaultOpen?: boolean;
    children: ReactNode;
}

const CycleSection = ({ icon, title, summary, defaultOpen = false, children }: CycleSectionProps) => {
    const [open, setOpen] = useState(defaultOpen);

    // a validation error that appears inside a collapsed section would otherwise disable Save with nothing visible explaining why
    useEffect(() => {
        if (defaultOpen) setOpen(true);
    }, [defaultOpen]);

    return (
        <div className="border rounded-8 mb-8">
            <button
                type="button"
                className="d-flex align-items-center gap-12 w-100 text-left border-0 bg-transparent p-12 cursorpointer"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
            >
                <SpirisIcon name={open ? 'chevron-down' : 'chevron-right'} size="sm" />
                <SpirisIcon name={icon} size="sm" />
                <span className="flex-1">
                    <span className="font-weight-600 d-block">{title}</span>
                    <span className="text-secondary font-size-xs d-block">{summary}</span>
                </span>
            </button>
            {open && <div className="border-top p-16">{children}</div>}
        </div>
    );
};

interface BillingCycleFieldsProps {
    idPrefix: string;
    cycle: PlanBillingCycleDraft;
    lengthError?: string;
    disabled: boolean;
    canRemoveNotBillableOffset: boolean;
    onChange: (cycle: PlanBillingCycleDraft) => void;
}

const BillingCycleFields = ({ idPrefix, cycle, lengthError, disabled, canRemoveNotBillableOffset, onChange }: BillingCycleFieldsProps) => {
    const { t } = useTranslation();

    return (
        <>
            <FormField
                label={t('packages.periodLength') ?? ''}
                htmlFor={`${idPrefix}-length`}
                error={isValidCycleLength(cycle.length) ? lengthError : (t('packages.lengthInvalid') ?? '')}
                help={t('packages.cycleLengthHelp') ?? ''}
            >
                <TimeLengthFields id={`${idPrefix}-length`} value={cycle.length} allowEmpty disabled={disabled} onChange={(length) => onChange({ ...cycle, length })} />
            </FormField>
            <OffsetFields
                idPrefix={`${idPrefix}-invoice`}
                label={t('packages.invoiceDateOffset') ?? ''}
                value={cycle.invoiceDateOffset}
                disabled={disabled}
                onChange={(invoiceDateOffset) => onChange({ ...cycle, invoiceDateOffset })}
            />
            <OffsetFields
                idPrefix={`${idPrefix}-renewal`}
                label={t('packages.renewalDateOffset') ?? ''}
                value={cycle.renewalDateOffset}
                disabled={disabled}
                onChange={(renewalDateOffset) => onChange({ ...cycle, renewalDateOffset })}
            />
            {cycle.notBillablePeriodRenewalDateOffset === null ? (
                !disabled && (
                    <button type="button" className="btn btn-tertiary btn-sm" onClick={() => onChange({ ...cycle, notBillablePeriodRenewalDateOffset: emptyNotBillableOffset() })}>
                        <SpirisIcon name="plus" size="sm" className="mr-8" />
                        {t('packages.addNotBillableOffset')}
                    </button>
                )
            ) : (
                <OffsetFields
                    idPrefix={`${idPrefix}-not-billable`}
                    label={t('packages.notBillableOffset') ?? ''}
                    value={cycle.notBillablePeriodRenewalDateOffset}
                    disabled={disabled}
                    onChange={(notBillablePeriodRenewalDateOffset) => onChange({ ...cycle, notBillablePeriodRenewalDateOffset })}
                    onRemove={canRemoveNotBillableOffset ? () => onChange({ ...cycle, notBillablePeriodRenewalDateOffset: null }) : undefined}
                />
            )}
        </>
    );
};

const PackagePlanEditorDrawer = ({ plan, copySource, limitedEditing, isSaving, onClose, onSave }: PackagePlanEditorDrawerProps) => {
    const { t } = useTranslation();
    const { formatCycleLength } = usePlanFormat();
    const [draft, setDraft] = useState<PlanDraft>(() =>
        plan !== undefined ? toPlanDraft(plan) : copySource !== undefined ? toCopiedPlanDraft(copySource.plan, t('packages.copySuffix') ?? '') : emptyPlanDraft()
    );

    const isNew = plan === undefined;
    const settingsLocked = limitedEditing && !isNew;
    const title = plan !== undefined ? t('packages.editPlan') : copySource !== undefined ? t('packages.copyPlanTitle') : t('packages.newPlan');
    const update = <K extends keyof PlanDraft>(key: K, value: PlanDraft[K]) => setDraft((prev) => ({ ...prev, [key]: value }));

    const billingSummary = (length: CycleLength) => (length === null ? (t('packages.evergreen') ?? '') : (t('packages.every', { length: formatCycleLength(length) }) ?? ''));

    const nameMissing = draft.name.trim().length === 0;
    const unitMismatch = hasSubscriptionUnitMismatch(draft);
    const usageMismatch = hasUsageLengthMismatch(draft);
    const canSave = isValidPlanDraft(draft) && !isSaving;

    return (
        <Drawer
            title={title ?? ''}
            subtitle={copySource === undefined ? (t('packages.planEditorSubtitle') ?? '') : (t('packages.copiedFromSubtitle', { package: copySource.packageName }) ?? '')}
            onClose={onClose}
            canClose={!isSaving}
            footer={
                <>
                    <button type="button" className="btn btn-default" disabled={isSaving} onClick={onClose}>
                        {t('common.cancel')}
                    </button>
                    <button type="button" className="btn btn-primary" disabled={!canSave} onClick={() => onSave(toPlanRequest(draft))}>
                        {isSaving && <span className="spinner spinner-sm mr-8" />}
                        {isNew ? t('packages.createPlan') : t('common.save')}
                    </button>
                </>
            }
        >
            {settingsLocked && <div className="alert alert-info mb-16">{t('packages.limitedPlanEditing')}</div>}
            {copySource !== undefined && (
                <div className="alert alert-info mb-16">{t('packages.copiedFrom', { plan: copySource.plan.name ?? '', package: copySource.packageName })}</div>
            )}

            <div className="d-flex flex-wrap gap-16">
                <FormField label={t('common.name') ?? ''} htmlFor="plan-name" required inline error={nameMissing ? (t('packages.nameRequired') ?? '') : undefined}>
                    <input id="plan-name" type="text" className="form-control" value={draft.name} onChange={(e) => update('name', e.target.value)} />
                </FormField>
                <FormField label={t('packages.externalId') ?? ''} htmlFor="plan-external-id" help={t('packages.externalIdHelp') ?? ''} inline>
                    <input id="plan-external-id" type="text" className="form-control" value={draft.externalId} onChange={(e) => update('externalId', e.target.value)} />
                </FormField>
            </div>

            <div className="d-flex flex-wrap gap-16">
                <FormField label={t('packages.periodEndAnchor') ?? ''} htmlFor="plan-period-end-anchor" required help={t('packages.periodEndAnchorHelp') ?? ''} inline>
                    <EnumSelect
                        id="plan-period-end-anchor"
                        disabled={settingsLocked}
                        value={draft.periodEndAnchor}
                        options={periodEndAnchors}
                        getLabel={(anchor) => t(VossPeriodEndAnchorText[anchor])}
                        onChange={(periodEndAnchor) => update('periodEndAnchor', periodEndAnchor)}
                    />
                </FormField>
                <FormField label={t('packages.changeBehaviour') ?? ''} htmlFor="plan-change-behaviour" help={t('packages.changeBehaviourHelp') ?? ''} inline>
                    <EnumSelect
                        id="plan-change-behaviour"
                        disabled={settingsLocked}
                        value={draft.changeBehaviour}
                        options={planChangeBehaviours}
                        getLabel={(behaviour) => t(VossPlanChangeBehaviourText[behaviour])}
                        emptyLabel="—"
                        onChange={(changeBehaviour) => update('changeBehaviour', changeBehaviour)}
                    />
                </FormField>
            </div>

            <h4 className="mt-16 font-16">{t('packages.cycles')}</h4>

            <CycleSection
                icon="lock"
                title={t('packages.bindingCycle') ?? ''}
                summary={`${formatCycleLength(draft.bindingPeriodCycle.length)} · ${t(VossBindingPeriodEndActionText[draft.bindingPeriodCycle.periodEndAction])}`}
                defaultOpen
            >
                <div className="d-flex flex-wrap gap-16">
                    <FormField
                        label={t('packages.length') ?? ''}
                        htmlFor="plan-binding-length"
                        inline
                        help={t('packages.cycleLengthHelp') ?? ''}
                        error={isValidCycleLength(draft.bindingPeriodCycle.length) ? undefined : (t('packages.lengthInvalid') ?? '')}
                    >
                        <TimeLengthFields
                            id="plan-binding-length"
                            value={draft.bindingPeriodCycle.length}
                            allowEmpty
                            disabled={settingsLocked}
                            onChange={(length) => update('bindingPeriodCycle', { ...draft.bindingPeriodCycle, length })}
                        />
                    </FormField>
                    <FormField label={t('packages.atPeriodEnd') ?? ''} htmlFor="plan-binding-end-action" required inline>
                        <EnumSelect
                            id="plan-binding-end-action"
                            disabled={settingsLocked}
                            value={draft.bindingPeriodCycle.periodEndAction}
                            options={bindingPeriodEndActions}
                            getLabel={(action) => t(VossBindingPeriodEndActionText[action])}
                            onChange={(periodEndAction) => update('bindingPeriodCycle', { ...draft.bindingPeriodCycle, periodEndAction })}
                        />
                    </FormField>
                </div>
            </CycleSection>

            <CycleSection
                icon="credit-card"
                title={t('packages.subscriptionBilling') ?? ''}
                summary={billingSummary(draft.subscriptionBillingPeriodCycle.length)}
                defaultOpen={unitMismatch}
            >
                <BillingCycleFields
                    idPrefix="plan-subscription"
                    cycle={draft.subscriptionBillingPeriodCycle}
                    lengthError={unitMismatch ? (t('packages.subscriptionUnitMismatch') ?? '') : undefined}
                    disabled={settingsLocked}
                    canRemoveNotBillableOffset={isNew}
                    onChange={(cycle) => update('subscriptionBillingPeriodCycle', cycle)}
                />
            </CycleSection>

            <CycleSection icon="chart-column" title={t('packages.usageBilling') ?? ''} summary={billingSummary(draft.usageBillingPeriodCycle.length)} defaultOpen={usageMismatch}>
                <BillingCycleFields
                    idPrefix="plan-usage"
                    cycle={draft.usageBillingPeriodCycle}
                    lengthError={usageMismatch ? (t('packages.usageLengthMismatch') ?? '') : undefined}
                    disabled={settingsLocked}
                    canRemoveNotBillableOffset={isNew}
                    onChange={(cycle) => update('usageBillingPeriodCycle', cycle)}
                />
            </CycleSection>
        </Drawer>
    );
};

export default PackagePlanEditorDrawer;
